import React, {
  FC,
  KeyboardEvent,
  MouseEvent,
  useContext,
  useImperativeHandle,
  useState,
} from "react";
import classNames from "classnames";
import { APIContext, EventsContext } from "@app/pippa/provider";
import { checkAttributeMatch } from "@app/pippa/helpers";
import {
  AttributeNames,
  AttributeValues,
  KeyTypes,
  QuickReplyType,
} from "@app/pippa/models";
import "./quickReply.scss";

interface Props {
  replies: QuickReplyType[];
}

const QuickReply: FC<Props> = ({ replies }) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [hideReplies, setHideReplies] = useState(false);
  const { userEventHandler, textMessageRef, quickReplyRef } =
    useContext(EventsContext);
  const {
    apiProviderState: { chatId },
  } = useContext(APIContext);

  const quickReplyButtonClasses = (reply: QuickReplyType) => {
    const hasQRSecondaryType = reply.type === "SECONDARY";

    return classNames("quick-reply__button pippa-quick-reply", {
      "quick-reply__button-secondary": hasQRSecondaryType,
    });
  };

  const quickReplyContainerClasses = classNames("quick-reply__list-container", {
    "hidden-reply": !showReplies,
    "without-reply": hideReplies,
    "animation-reply": showAnimation,
  });

  const onClickHandler = (
    e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
    reply: QuickReplyType
  ) => {
    e.currentTarget.disabled = true;

    const hasUpdateMessages = checkAttributeMatch(
      reply.payload,
      AttributeNames.UPDATE_MESSAGES,
      AttributeValues.FALSE
    );

    const handlePostbackEvent = () => {
      userEventHandler(reply.title, chatId);
    };

    if (hasUpdateMessages) {
      e.currentTarget.disabled = false;
      handlePostbackEvent();
    } else {
      setTimeout(() => {
        setShowReplies(false);
        handlePostbackEvent();
        textMessageRef?.current?.disableRating();
      }, 650);
      setShowAnimation(true);
    }
  };

  const onKeyDownHandler = (
    e: KeyboardEvent<HTMLButtonElement>,
    reply: QuickReplyType
  ) => {
    const currentKey = e.key.toLowerCase();
    if (currentKey === KeyTypes.ENTER) {
      e.preventDefault();
      onClickHandler(e, reply);
    }
  };

  useImperativeHandle(quickReplyRef, () => ({
    removeQuickReplies() {
      setTimeout(() => {
        setShowReplies(false);
        setHideReplies(true);
      }, 650);
      setShowAnimation(true);
    },
  }));

  return (
    <ul className={quickReplyContainerClasses}>
      {replies.map((reply, index) => (
        <li
          key={`${index}-${reply.payload}`}
          className="quick-reply__list-item"
        >
          <button
            data-testid="quick-reply"
            className={quickReplyButtonClasses(reply)}
            onClick={(e) => onClickHandler(e, reply)}
            onKeyDown={(e) => onKeyDownHandler(e, reply)}
          >
            <span className="quick-reply__button-title">{reply.title}</span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default QuickReply;
